import { type ReactNode, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useTechnicianUpcomingVisits } from '@/hooks/technicians/useTechnicianUpcomingVisits';
import { apiDelete, apiPatch, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerTechnician } from '@/types/scheduler';

/**
 * GAP-02 — the ONE source of truth for deactivating/deleting a technician
 * (choice, never a block). Shared by the roster row actions and the technician
 * detail page so the two can't diverge. A tech with upcoming visits gets a
 * 3-option dialog (reassign first / remove anyway / cancel); the backend frees
 * any upcoming visits either way (needs-tech), never orphaning them.
 */
export function useTechnicianRemoval(
  technician: Pick<SchedulerTechnician, 'id' | 'name'>,
  opts?: {
    /** After a delete (the tech left the roster) — e.g. navigate away from a detail page. */
    onDeleted?: () => void;
    /** After activate/deactivate — e.g. refetch a locally-held technician record. */
    onChanged?: () => void;
  },
) {
  const [showDelete, setShowDelete] = useState(false);
  // Which mutation the user asked for; we first look up the upcoming-visit count
  // to decide between a direct proceed and the 3-option choice.
  const [checking, setChecking] = useState<null | 'delete' | 'deactivate'>(null);
  const [choice, setChoice] = useState<{ action: 'delete' | 'deactivate'; count: number } | null>(
    null,
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.detail(technician.id) });
  };

  const goToOffboarding = (action: 'delete' | 'deactivate') =>
    void navigate({
      to: '/dashboard/technicians/$id/offboarding',
      params: { id: technician.id },
      search: { action },
    });

  // Lazy upcoming-visit count, fetched only once the user picks Delete/Deactivate.
  const upcomingCheck = useTechnicianUpcomingVisits(technician.id, checking !== null);

  const toggleActiveMutation = useApiMutation<
    SchedulerTechnician,
    { id: string; isActive: boolean }
  >({
    mutationFn: ({ id, isActive }) =>
      apiPatch<SchedulerTechnician>(operationsClient, schedulerEndpoints.technician(id), {
        isActive,
      }),
    onSuccess: (_data, { isActive }) => {
      invalidate();
      setChoice(null);
      toast.success(`Technician ${isActive ? 'activated' : 'deactivated'}`);
      opts?.onChanged?.();
    },
  });

  const deleteMutation = useApiMutation<void, { id: string }>({
    mutationFn: ({ id }) => apiDelete(operationsClient, schedulerEndpoints.technician(id)),
    onSuccess: () => {
      invalidate();
      // The backend soft-deletes (deactivates) a tech with history and hard-deletes
      // an unused one; either way the record leaves the active roster and any
      // upcoming visits are freed (needs-tech), never orphaned.
      toast.success('Technician removed');
      setShowDelete(false);
      setChoice(null);
      opts?.onDeleted?.();
    },
  });

  // Resolve a pending Delete/Deactivate once the upcoming-visit count loads. With
  // future work, offer the 3-option choice — never a block. With none, proceed.
  useEffect(() => {
    if (!checking) return;
    if (upcomingCheck.isError) {
      // Count unavailable — proceed anyway; the BE frees any upcoming visits.
      if (checking === 'delete') setShowDelete(true);
      else toggleActiveMutation.mutate({ id: technician.id, isActive: false });
      setChecking(null);
      return;
    }
    if (!upcomingCheck.isSuccess) return;
    const count = upcomingCheck.data.length;
    if (count > 0) {
      setChoice({ action: checking, count });
    } else if (checking === 'delete') {
      setShowDelete(true);
    } else {
      toggleActiveMutation.mutate({ id: technician.id, isActive: false });
    }
    setChecking(null);
    // toggleActiveMutation is stable enough for this one-shot resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, upcomingCheck.isSuccess, upcomingCheck.isError, upcomingCheck.data]);

  const isMutating = toggleActiveMutation.isPending || deleteMutation.isPending;

  const dialogs: ReactNode = (
    <>
      {/* A tech with future work: choose, never blocked. */}
      <Dialog open={choice !== null} onOpenChange={(open) => !open && setChoice(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {technician.name} has {choice?.count ?? 0} upcoming visit
              {choice?.count === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription>
              Reassign them to someone else, or remove {technician.name} anyway — their future
              visits will be left without a technician.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              variant="outline"
              onClick={() => {
                const action = choice?.action ?? 'deactivate';
                setChoice(null);
                goToOffboarding(action);
              }}
            >
              Reassign now →
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="ghost" onClick={() => setChoice(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={isMutating}
                onClick={() => {
                  if (choice?.action === 'delete') {
                    deleteMutation.mutate({ id: technician.id });
                  } else {
                    toggleActiveMutation.mutate({ id: technician.id, isActive: false });
                  }
                }}
              >
                {isMutating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {choice?.action === 'delete' ? 'Delete anyway' : 'Deactivate anyway'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Technician</DialogTitle>
            <DialogDescription>
              Delete <strong>{technician.name}</strong>? If they have job history, the record is
              kept and the technician is deactivated to preserve it. Only a technician with no
              visits is fully removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate({ id: technician.id })}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return {
    /** Deactivate flow: looks up upcoming visits, then choice-or-direct. */
    requestDeactivate: () => setChecking('deactivate'),
    /** Delete flow: looks up upcoming visits, then choice-or-history-aware-confirm. */
    requestDelete: () => setChecking('delete'),
    /** Reactivate — always safe, no check. */
    activate: () => toggleActiveMutation.mutate({ id: technician.id, isActive: true }),
    /** Which flow is resolving its upcoming-visit count (for button spinners). */
    checking,
    isMutating,
    /** Render this once in the consumer — the choice + delete-confirm dialogs. */
    dialogs,
  };
}
